import { dayjsUTC } from "../../utils/dates"
import { extraColors } from "../../utils/theme"
import type { PrecomputedBiothreatSignal } from "./types"
import type { NwssSignalSample } from "./nwssSampleTypes"
import { reportingCountiesForSample } from "./nwssCountyMatch"
import {
  formatNwssChartTitle,
  formatNwssRollingRateChartTitle,
} from "./nwssDisplayFormat"

export const NWSS_DRAWER_CHART_LOOKBACK_MONTHS = 24

/** Site Detections tab (bubble timeline + site-tab rolling rate). */
export const NWSS_SITE_DETECTIONS_LOOKBACK_MONTHS = 12

const MIN_CONTEXT_DAYS = 180
const MAX_CONTEXT_DAYS = 730
const CONTEXT_BUFFER_DAYS = 30

export interface NwssSiteBubbleTimelineData {
  sites: string[]
  weekKeys: string[]
  weekLabels: string[]
  cells: Map<string, number>
}

export interface NwssRollingRatePoint {
  date: string
  ratePct: number
  markerTier: 0 | 1 | 2
  sameDayDetections: number
  windowSampleCount: number
  windowDetectionCount: number
}

export interface NwssRollingDetectionRateData {
  geoLabel: string
  /** When set, only samples linked to this reporting county are included. */
  reportingCounty: string | null
  points: NwssRollingRatePoint[]
  empty: boolean
}

/** API ``context_days`` padding from event span (capped ~2 years). */
export const computeNwssSamplesContextDays = (
  meta: PrecomputedBiothreatSignal["nwss_meta"]
): number => {
  if (!meta?.start_date || !meta.last_detection_date) {
    return MIN_CONTEXT_DAYS
  }
  const start = dayjsUTC(meta.start_date)
  const end = dayjsUTC(meta.last_detection_date)
  const eventSpanDays = Math.max(1, end.diff(start, "day") + 1)
  return Math.min(
    MAX_CONTEXT_DAYS,
    Math.max(MIN_CONTEXT_DAYS, eventSpanDays + CONTEXT_BUFFER_DAYS)
  )
}

export const weekEndKey = (date: string): string =>
  dayjsUTC(date).endOf("week").format("YYYY-MM-DD")

/** Last N calendar months ending at the latest sample in the set. */
export const filterSamplesToChartLookback = (
  samples: NwssSignalSample[],
  months = NWSS_DRAWER_CHART_LOOKBACK_MONTHS
): NwssSignalSample[] => {
  if (samples.length === 0) return []

  const latestDate = samples.reduce<string | null>(
    (max, sample) =>
      !max || sample.sample_collect_date > max
        ? sample.sample_collect_date
        : max,
    null
  )
  if (!latestDate) return []

  const chartEnd = dayjsUTC(latestDate)
  const chartStart = chartEnd.subtract(months, "month")
  return samples.filter(sample => {
    const date = dayjsUTC(sample.sample_collect_date)
    return !date.isBefore(chartStart, "day") && !date.isAfter(chartEnd, "day")
  })
}

export const nwssChartTitle = formatNwssChartTitle

export const nwssRollingRateChartTitle = formatNwssRollingRateChartTitle

/** Y-axis max and tick values scaled to the series (capped at 100%). */
export const computeRollingRateYAxis = (
  points: NwssRollingRatePoint[]
): { max: number; ticks: number[] } => {
  const peak = points.reduce((max, point) => Math.max(max, point.ratePct), 0)
  if (peak <= 0) {
    return { max: 20, ticks: [0, 5, 10, 15, 20] }
  }

  const padded = Math.min(100, Math.ceil((peak * 1.15) / 5) * 5)
  const max = Math.max(10, padded)
  const step = max <= 20 ? 5 : max <= 50 ? 10 : 20
  const ticks: number[] = []
  for (let tick = 0; tick <= max; tick += step) {
    ticks.push(tick)
  }
  return { max, ticks }
}

/** Y-axis for event-scope rolling charts (detection count in trailing 14 days). */
export const computeRollingCountYAxis = (
  points: NwssRollingRatePoint[]
): { max: number; ticks: number[] } => {
  const peak = points.reduce(
    (max, point) => Math.max(max, point.windowDetectionCount),
    0
  )
  if (peak <= 0) {
    return { max: 5, ticks: [0, 1, 2, 3, 4, 5] }
  }

  const padded = Math.ceil(peak * 1.15)
  const step = padded <= 5 ? 1 : padded <= 12 ? 2 : padded <= 25 ? 5 : 10
  const max = Math.max(3, Math.ceil(padded / step) * step)
  const ticks: number[] = []
  for (let tick = 0; tick <= max; tick += step) {
    ticks.push(tick)
  }
  return { max, ticks }
}

export const buildSiteBubbleTimeline = (
  samples: NwssSignalSample[],
  siteRows: string[]
): NwssSiteBubbleTimelineData => {
  const windowSamples = filterSamplesToChartLookback(
    samples,
    NWSS_SITE_DETECTIONS_LOOKBACK_MONTHS
  )
  const { weeks, cells } = accumulateWeeklyDetectionCells(
    windowSamples,
    sample => [sample.site]
  )
  const grid = formatWeeklyGrid(siteRows, weeks, cells)
  return { sites: siteRows, ...grid }
}

export const scopeNwssSamplesForRollingChart = (
  samples: NwssSignalSample[],
  reportingCounty: string | null,
  lookbackMonths = NWSS_DRAWER_CHART_LOOKBACK_MONTHS
): NwssSignalSample[] => {
  const windowSamples = filterSamplesToChartLookback(samples, lookbackMonths)
  if (!reportingCounty) return windowSamples
  return windowSamples.filter(
    sample => reportingCountiesForSample(sample, [reportingCounty]).length > 0
  )
}

export const buildRollingDetectionRate = (
  samples: NwssSignalSample[],
  geoLabel: string,
  reportingCounty: string | null,
  lookbackMonths = NWSS_DRAWER_CHART_LOOKBACK_MONTHS
): NwssRollingDetectionRateData => {
  const scoped = scopeNwssSamplesForRollingChart(
    samples,
    reportingCounty,
    lookbackMonths
  )

  const anchorDates = [
    ...new Set(scoped.map(sample => sample.sample_collect_date)),
  ].sort()

  if (anchorDates.length === 0) {
    return { geoLabel, reportingCounty, points: [], empty: true }
  }

  const points: NwssRollingRatePoint[] = []
  for (const anchor of anchorDates) {
    const anchorDay = dayjsUTC(anchor)
    const windowStart = anchorDay.subtract(13, "day")
    const inWindow = scoped.filter(sample => {
      const day = dayjsUTC(sample.sample_collect_date)
      return !day.isBefore(windowStart, "day") && !day.isAfter(anchorDay, "day")
    })
    if (inWindow.length === 0) continue

    const windowDetectionCount = inWindow.filter(
      sample => sample.is_detected
    ).length
    const sameDayDetections = scoped.filter(
      sample => sample.sample_collect_date === anchor && sample.is_detected
    ).length

    points.push({
      date: anchor,
      ratePct: (100 * windowDetectionCount) / inWindow.length,
      markerTier: sameDayDetectionTier(sameDayDetections),
      sameDayDetections,
      windowSampleCount: inWindow.length,
      windowDetectionCount,
    })
  }

  return {
    geoLabel,
    reportingCounty,
    points,
    empty: points.length === 0,
  }
}

export const buildCountyRollingRates = (
  samples: NwssSignalSample[],
  reportingCounties: string[] | null,
  fallbackGeo: string,
  lookbackMonths = NWSS_DRAWER_CHART_LOOKBACK_MONTHS
): NwssRollingDetectionRateData[] => {
  if (reportingCounties?.length) {
    return reportingCounties.map(county =>
      buildRollingDetectionRate(samples, county, county, lookbackMonths)
    )
  }
  return [buildRollingDetectionRate(samples, fallbackGeo, null, lookbackMonths)]
}

/** Bubble size/color tier 0–5 (5+ capped). */
export const bubbleTierFromCount = (count: number): number =>
  count <= 0 ? 0 : Math.min(5, count)

export const bubbleDiameterPx = (tier: number): number => {
  if (tier <= 0) return 8
  return 8 + tier * 3
}

/** Heatmap detection red with the same tier alphas as the original blue bubbles. */
const BUBBLE_DETECTION_FILL_RGB = "255, 75, 85"
const BUBBLE_DETECTION_STROKE_RGB = "180, 40, 50"

export const bubbleDetectionFillColor = (tier: number): string => {
  if (tier <= 0) return extraColors.translucent5
  const alpha = Math.min(1, 0.35 + tier * 0.13)
  return `rgba(${BUBBLE_DETECTION_FILL_RGB}, ${alpha})`
}

export const bubbleDetectionStrokeColor = (tier: number): string => {
  if (tier <= 0) return extraColors.translucent50
  const alpha = Math.min(1, 0.5 + tier * 0.1)
  return `rgba(${BUBBLE_DETECTION_STROKE_RGB}, ${alpha})`
}

export const sortSiteRows = (sites: string[]): string[] =>
  [...sites].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })
  )

const accumulateWeeklyDetectionCells = (
  samples: NwssSignalSample[],
  rowKeysForSample: (sample: NwssSignalSample) => string[]
): { weeks: Set<string>; cells: Map<string, number> } => {
  const weeks = new Set<string>()
  const cells = new Map<string, number>()
  for (const sample of samples) {
    const week = weekEndKey(sample.sample_collect_date)
    weeks.add(week)
    if (!sample.is_detected) continue
    for (const row of rowKeysForSample(sample)) {
      const cellKey = `${row}__${week}`
      cells.set(cellKey, (cells.get(cellKey) ?? 0) + 1)
    }
  }
  return { weeks, cells }
}

const formatWeeklyGrid = (
  rowLabels: string[],
  weeks: Set<string>,
  cells: Map<string, number>
): Pick<NwssSiteBubbleTimelineData, "weekKeys" | "weekLabels" | "cells"> => {
  const weekKeys = [...weeks].sort()
  return {
    weekKeys,
    weekLabels: weekKeys.map(week => dayjsUTC(week).format("MMM D")),
    cells,
  }
}

const sameDayDetectionTier = (count: number): 0 | 1 | 2 => {
  if (count <= 0) return 0
  if (count === 1) return 1
  return 2
}
